import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
  Switch,
  Modal,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { wakeDetailsService } from '../../../../services/wakeDetailsInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('WakeDetailsInput');

type WakeDetailsInputScreenRouteProp = RouteProp<RootStackParamList, 'WakeDetailsInput'>;

const WAKE_TYPE_OPTIONS = [
  'Public',
  'Private',
  'Family-only'
];

export const WakeDetailsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<WakeDetailsInputScreenRouteProp>();

  const { wakeDetailsData, isEditing } = route.params || {};

  const [wakeType, setWakeType] = useState(wakeDetailsData?.wakeType || '');
  const [wakeLocationPreference, setWakeLocationPreference] = useState(wakeDetailsData?.wakeLocationPreference || false);
  const [wakeLocation, setWakeLocation] = useState(wakeDetailsData?.wakeLocation || '');
  const [casketDisplayPreference, setCasketDisplayPreference] = useState(wakeDetailsData?.casketDisplayPreference || false);
  const [casketDisplay, setCasketDisplay] = useState(wakeDetailsData?.casketDisplay || '');
  const [shareMemories, setShareMemories] = useState(wakeDetailsData?.shareMemories || false);
  const [favoriteOutfitPreference, setFavoriteOutfitPreference] = useState(wakeDetailsData?.favoriteOutfitPreference || false);
  const [favoriteOutfit, setFavoriteOutfit] = useState(wakeDetailsData?.favoriteOutfit || '');
  const [favoritePhotosPreference, setFavoritePhotosPreference] = useState(wakeDetailsData?.favoritePhotosPreference || false);
  const [favoritePhotos, setFavoritePhotos] = useState(wakeDetailsData?.favoritePhotos || '');
  const [favoriteArtPreference, setFavoriteArtPreference] = useState(wakeDetailsData?.favoriteArtPreference || false);
  const [favoriteArt, setFavoriteArt] = useState(wakeDetailsData?.favoriteArt || '');
  const [favoriteVideosPreference, setFavoriteVideosPreference] = useState(wakeDetailsData?.favoriteVideosPreference || false);
  const [favoriteVideos, setFavoriteVideos] = useState(wakeDetailsData?.favoriteVideos || '');
  const [readingsPreference, setReadingsPreference] = useState(wakeDetailsData?.readingsPreference || false);
  const [readingsName, setReadingsName] = useState(wakeDetailsData?.readingsName || '');
  const [specialRequests, setSpecialRequests] = useState(wakeDetailsData?.specialRequests || '');
  const [saving, setSaving] = useState(false);
  const [showWakeTypeModal, setShowWakeTypeModal] = useState(false);

  const handleSave = async () => {
    if (!wakeType.trim()) {
      Alert.alert('Error', 'Please select the wake type (public, private, or family-only)');
      return;
    }

    try {
      setSaving(true);

      const wakeDetailsUpdateData = {
        wakeType: wakeType.trim(),
        wakeLocationPreference,
        wakeLocation: wakeLocationPreference ? wakeLocation.trim() : '',
        casketDisplayPreference,
        casketDisplay: casketDisplayPreference ? casketDisplay.trim() : '',
        shareMemories,
        favoriteOutfitPreference,
        favoriteOutfit: favoriteOutfitPreference ? favoriteOutfit.trim() : '',
        favoritePhotosPreference,
        favoritePhotos: favoritePhotosPreference ? favoritePhotos.trim() : '',
        favoriteArtPreference,
        favoriteArt: favoriteArtPreference ? favoriteArt.trim() : '',
        favoriteVideosPreference,
        favoriteVideos: favoriteVideosPreference ? favoriteVideos.trim() : '',
        readingsPreference,
        readingsName: readingsPreference ? readingsName.trim() : '',
        specialRequests: specialRequests.trim(),
      };

      let savedWakeDetails;

      if (isEditing && wakeDetailsData?.id) {
        // Update existing record
        savedWakeDetails = await wakeDetailsService.updateWakeDetails(
          wakeDetailsData.id,
          wakeDetailsUpdateData
        );
        // Handle wrapped response format { success, data, message }
        const updatedData = savedWakeDetails?.data || savedWakeDetails;
        logger.debug('Wake details updated successfully with ID:', updatedData.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        navigation.navigate('WakeDetailsLockUnlock', { wakeDetailsId: updatedData.id });
      } else {
        // Create new record
        savedWakeDetails = await wakeDetailsService.createWakeDetails(wakeDetailsUpdateData);
        // Handle wrapped response format { success, data, message }
        const createdData = savedWakeDetails?.data || savedWakeDetails;
        logger.debug('Wake details saved successfully with ID:', createdData.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('WakeDetailsLockUnlock', { wakeDetailsId: createdData.id });
      }
    } catch (error) {
      logger.error('Error saving wake details:', error);
      Alert.alert('Error', 'Failed to save wake details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const selectWakeType = (type: string) => {
    setWakeType(type);
    setShowWakeTypeModal(false);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Wake Details
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          {/* Wake Type Dropdown */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Do you want your wake to be public, private, or family-only?</Text>
            <TouchableOpacity
              style={styles.input}
              onPress={() => setShowWakeTypeModal(true)}
            >
              <Text style={[styles.inputText, !wakeType && styles.placeholderText]}>
                {wakeType || 'Select wake type'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={styles.dropdownIcon}
              />
            </TouchableOpacity>
          </View>

          {/* Wake Location Preference */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Do you have a preference on where the wake is held?</Text>
            <View style={styles.switchContainer}>
              <Switch
                value={wakeLocationPreference}
                onValueChange={setWakeLocationPreference}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={wakeLocationPreference ? '#FFFFFF' : '#FFFFFF'}
              />
              <Text style={styles.switchLabel}>
                {wakeLocationPreference ? 'Yes, I have a preference' : 'No preference'}
              </Text>
            </View>
            {wakeLocationPreference && (
              <TextInput
                style={[styles.input, styles.textArea]}
                value={wakeLocation}
                onChangeText={setWakeLocation}
                placeholder="Specify your preference (e.g., funeral home, church, home, other location)"
                placeholderTextColor="#A9A9A9"
                multiline={true}
                numberOfLines={3}
                textAlignVertical="top"
              />
            )}
          </View>

          {/* Casket Display Preference */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Do you have a preference on casket display?</Text>
            <View style={styles.switchContainer}>
              <Switch
                value={casketDisplayPreference}
                onValueChange={setCasketDisplayPreference}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={casketDisplayPreference ? '#FFFFFF' : '#FFFFFF'}
              />
              <Text style={styles.switchLabel}>
                {casketDisplayPreference ? 'Yes, I have a preference' : 'No preference'}
              </Text>
            </View>
            {casketDisplayPreference && (
              <TextInput
                style={[styles.input, styles.textArea]}
                value={casketDisplay}
                onChangeText={setCasketDisplay}
                placeholder="Specify your preference (e.g., open casket, closed casket, urn display, no remains present)"
                placeholderTextColor="#A9A9A9"
                multiline={true}
                numberOfLines={3}
                textAlignVertical="top"
              />
            )}
          </View>

          {/* Share Memories */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Would you like people to share memories or stories?</Text>
            <View style={styles.switchContainer}>
              <Switch
                value={shareMemories}
                onValueChange={setShareMemories}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={shareMemories ? '#FFFFFF' : '#FFFFFF'}
              />
              <Text style={styles.switchLabel}>
                {shareMemories ? 'Yes, encourage memory sharing' : 'No memory sharing'}
              </Text>
            </View>
          </View>

          {/* Favorite Outfit Preference */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Do you have a favorite outfit?</Text>
            <View style={styles.switchContainer}>
              <Switch
                value={favoriteOutfitPreference}
                onValueChange={setFavoriteOutfitPreference}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={favoriteOutfitPreference ? '#FFFFFF' : '#FFFFFF'}
              />
              <Text style={styles.switchLabel}>
                {favoriteOutfitPreference ? 'Yes, I have a favorite outfit' : 'No specific outfit'}
              </Text>
            </View>
            {favoriteOutfitPreference && (
              <TextInput
                style={styles.input}
                value={favoriteOutfit}
                onChangeText={setFavoriteOutfit}
                placeholder="Describe your favorite outfit"
                placeholderTextColor="#A9A9A9"
              />
            )}
          </View>

          {/* Favorite Photos Preference */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Do you have favorite photos?</Text>
            <View style={styles.switchContainer}>
              <Switch
                value={favoritePhotosPreference}
                onValueChange={setFavoritePhotosPreference}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={favoritePhotosPreference ? '#FFFFFF' : '#FFFFFF'}
              />
              <Text style={styles.switchLabel}>
                {favoritePhotosPreference ? 'Yes, I have favorite photos' : 'No specific photos'}
              </Text>
            </View>
            {favoritePhotosPreference && (
              <TextInput
                style={[styles.input, styles.textArea]}
                value={favoritePhotos}
                onChangeText={setFavoritePhotos}
                placeholder="Describe photo locations or types of photos you'd like displayed"
                placeholderTextColor="#A9A9A9"
                multiline={true}
                numberOfLines={3}
                textAlignVertical="top"
              />
            )}
          </View>

          {/* Favorite Art Preference */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Do you have favorite art?</Text>
            <View style={styles.switchContainer}>
              <Switch
                value={favoriteArtPreference}
                onValueChange={setFavoriteArtPreference}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={favoriteArtPreference ? '#FFFFFF' : '#FFFFFF'}
              />
              <Text style={styles.switchLabel}>
                {favoriteArtPreference ? 'Yes, I have favorite art' : 'No specific art'}
              </Text>
            </View>
            {favoriteArtPreference && (
              <TextInput
                style={styles.input}
                value={favoriteArt}
                onChangeText={setFavoriteArt}
                placeholder="Name or describe your favorite art pieces"
                placeholderTextColor="#A9A9A9"
              />
            )}
          </View>

          {/* Favorite Videos Preference */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Do you have any favorite videos?</Text>
            <View style={styles.switchContainer}>
              <Switch
                value={favoriteVideosPreference}
                onValueChange={setFavoriteVideosPreference}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={favoriteVideosPreference ? '#FFFFFF' : '#FFFFFF'}
              />
              <Text style={styles.switchLabel}>
                {favoriteVideosPreference ? 'Yes, I have favorite videos' : 'No specific videos'}
              </Text>
            </View>
            {favoriteVideosPreference && (
              <TextInput
                style={[styles.input, styles.textArea]}
                value={favoriteVideos}
                onChangeText={setFavoriteVideos}
                placeholder="Describe video locations or types of videos you'd like shown"
                placeholderTextColor="#A9A9A9"
                multiline={true}
                numberOfLines={3}
                textAlignVertical="top"
              />
            )}
          </View>

          {/* Readings Preference */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Do you want someone to do readings?</Text>
            <View style={styles.switchContainer}>
              <Switch
                value={readingsPreference}
                onValueChange={setReadingsPreference}
                trackColor={{ false: '#E5E5E5', true: '#29BFC0' }}
                thumbColor={readingsPreference ? '#FFFFFF' : '#FFFFFF'}
              />
              <Text style={styles.switchLabel}>
                {readingsPreference ? 'Yes, I want readings' : 'No readings'}
              </Text>
            </View>
            {readingsPreference && (
              <TextInput
                style={styles.input}
                value={readingsName}
                onChangeText={setReadingsName}
                placeholder="Name of person to do readings"
                placeholderTextColor="#A9A9A9"
              />
            )}
          </View>

          {/* Special Requests */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Other Special Requests:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specialRequests}
              onChangeText={setSpecialRequests}
              placeholder="Any other special requests for your wake"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      {/* Wake Type Modal */}
      <Modal
        visible={showWakeTypeModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowWakeTypeModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Wake Type</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowWakeTypeModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {WAKE_TYPE_OPTIONS.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={styles.stateItem}
                  onPress={() => selectWakeType(type)}
                >
                  <Text style={styles.stateItemText}>{type}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  placeholderText: {
    color: '#A9A9A9',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    marginBottom: ms(8),
  },
  switchLabel: {
    marginLeft: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
});

export default WakeDetailsInputScreen;